<template>
    <div class="row">
        <div class="col-md-6">
            <DatePicker ref="startDatePicker" v-model="startDate" :options="{ clearBtn: false }" />
        </div>
        <div class="col-md-6">
            <TimePicker
                ref="startTimePicker"
                v-model="startTime"
                :options="{
                    minuteStep: 1,
                    secondStep: 1,
                    maxHours: 24,
                    defaultTime: 'current',
                }"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { DateTime } from "@/data/util/DateTime";
    import { computed, ref, watch } from "vue";

    import DatePicker from "@/portal/components/elements/DatePicker.vue";
    import TimePicker from "@/portal/components/elements/TimePicker.vue";

    const model = defineModel<Date | undefined>({ required: true });

    defineProps<{
        required?: boolean;
    }>();

    const startDate = ref<Date | undefined>();
    const startTime = ref<string | undefined>();

    if (model.value) {
        const initStartDateUtc = new Date(
            Date.UTC(model.value.getFullYear(), model.value.getMonth(), model.value.getDate())
        );
        const initStartTime = DateTime.secondsToTimespan(DateTime.secondsSinceMidnight(model.value));

        startDate.value = initStartDateUtc;
        startTime.value = initStartTime;
    }

    const startDateTime = computed(() => {
        if (!startDate.value || !startTime.value) return undefined;

        const baseDate = new Date(startDate.value.getFullYear(), startDate.value.getMonth(), startDate.value.getDate());
        const seconds = DateTime.secondsOfTimeSpan(startTime.value);

        baseDate.setHours(0, 0, 0, 0);
        baseDate.setSeconds(seconds);

        return baseDate;
    });

    const startDatePicker = ref<InstanceType<typeof DatePicker> | null>(null);
    const startTimePicker = ref<InstanceType<typeof TimePicker> | null>(null);

    watch(startDateTime, () => {
        model.value = startDateTime.value;
    });

    function setError(hasError: boolean) {
        startDatePicker.value?.setError(hasError);
        startTimePicker.value?.setError(hasError);
    }

    defineExpose({
        setError,
    });
</script>
